import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, CalendarDays, RefreshCw, Store, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/common/EmptyState";
import { DataScope, monthOf } from "@/components/common/DataScope";
import { PosCard, RankTable } from "@/components/pos/PosTable";
import PosKpiRow from "@/components/pos/PosKpiRow";
import PosHourlyPanel from "@/components/pos/PosHourlyPanel";
import PosItemsPanel from "@/components/pos/PosItemsPanel";
import PosCategoriesPanel from "@/components/pos/PosCategoriesPanel";
import PosChannelsPanel from "@/components/pos/PosChannelsPanel";
import PosStaffPanel from "@/components/pos/PosStaffPanel";
import PosCashierCheckPanel from "@/components/pos/PosCashierCheckPanel";
import PosDataPanel from "@/components/pos/PosDataPanel";
import { isoFromDate, dateFromIso } from "@/lib/format";
import { OPS } from "@/constants/testIds";

const PI = OPS.posInsights;

function shift(days) {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return isoFromDate(d);
}

const PRESETS = {
    p7: () => ({ start: shift(7), end: shift(1) }),
    p30: () => ({ start: shift(30), end: shift(1) }),
    month: () => {
        const now = new Date();
        return { start: isoFromDate(new Date(now.getFullYear(), now.getMonth(), 1)), end: shift(1) };
    },
};

function DateField({ testId, label, value, onChange }) {
    const [open, setOpen] = useState(false);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button data-testid={testId} variant="outline"
                    className="h-9 justify-start gap-2 surface border-[color:var(--border-soft)] font-normal">
                    <CalendarDays className="h-4 w-4 text-[color:var(--brand-brown)]" />
                    <span className="text-xs text-[color:var(--ink-muted)]">{label}</span>
                    <bdi dir="ltr" className="tnum">{value}</bdi>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dateFromIso(value)}
                    onSelect={(d) => { if (d) onChange(isoFromDate(d)); setOpen(false); }} initialFocus />
            </PopoverContent>
        </Popover>
    );
}

export default function PosInsightsPage() {
    const { t, lang } = useI18n();
    const [range, setRange] = useState(PRESETS.p30());
    const [branchId, setBranchId] = useState("all");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/pos-insights/overview", {
                params: {
                    start: range.start, end: range.end,
                    ...(branchId !== "all" ? { branch_ids: branchId } : {}),
                },
            });
            setData(r.data);
            if (r.data?.branches?.length) setBranches(r.data.branches);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [range.start, range.end, branchId]);

    useEffect(() => { load(); }, [load]);

    const branchRows = useMemo(() => (data?.per_branch || []).map((b) => ({
        ...b, name: lang === "ar" ? b.name_ar || b.name_en : b.name_en || b.name_ar,
    })), [data, lang]);

    const triggerClass =
        "gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-150 hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white sm:text-sm";

    return (
        <DataScope month={monthOf(range.end)}>
        <div data-testid={PI.page} className="fin-scope space-y-5">
            <PageHeader
                icon={Activity}
                title={t("pi_title")}
                subtitle={t("pi_subtitle")}
                actions={
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1 rounded-full border border-[color:var(--border-soft)] p-0.5">
                            {[["p7", t("pi_preset_7")], ["p30", t("pi_preset_30")], ["month", t("pi_preset_month")]].map(([k, label]) => (
                                <button key={k} data-testid={PI.preset(k)} onClick={() => setRange(PRESETS[k]())}
                                    className="rounded-full px-3 py-1 text-xs font-semibold text-[color:var(--ink-muted)] transition-colors hover:bg-[color:var(--brand-orange-soft)]">
                                    {label}
                                </button>
                            ))}
                        </div>
                        <DateField testId={PI.fromDate} label={t("pi_from")} value={range.start}
                            onChange={(v) => setRange((r) => ({ ...r, start: v }))} />
                        <DateField testId={PI.toDate} label={t("pi_to")} value={range.end}
                            onChange={(v) => setRange((r) => ({ ...r, end: v }))} />
                        <Select value={branchId} onValueChange={setBranchId}>
                            <SelectTrigger data-testid={PI.branchFilter} className="h-9 w-[190px] text-sm">
                                <Store className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                <SelectValue placeholder={t("pi_branch_all")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("pi_branch_all")}</SelectItem>
                                {branches.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>
                                        {lang === "ar" ? b.name_ar || b.name_en : b.name_en || b.name_ar}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button data-testid={PI.refresh} variant="outline" className="h-9 gap-2" onClick={load} disabled={loading}>
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                    </div>
                }
            />

            {loading && !data ? (
                <div data-testid={PI.loading} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                        {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-[86px] w-full rounded-2xl" />)}
                    </div>
                    <Skeleton className="h-[280px] w-full rounded-2xl" />
                </div>
            ) : !data?.available ? (
                <EmptyState
                    testId={PI.empty}
                    title={data?.reason === "no_permitted_branches" ? t("pi_no_branches") : t("pi_empty_title")}
                    hint={t("pi_empty_hint")}
                    dataset={data?.reason === "no_permitted_branches" ? undefined : "pos_branch_daily"}
                />
            ) : (
                <>
                    <PosKpiRow data={data} />

                    <PosCard icon={Store} title={t("pi_col_branch")} desc={t("pi_hourly_desc")}>
                        <RankTable
                            testId={PI.branchTable}
                            rows={branchRows}
                            columns={[
                                { key: "name", label: t("pi_col_branch"), strong: true },
                                { key: "transactions", label: t("pi_kpi_transactions"), type: "int" },
                                { key: "gross", label: t("pi_kpi_gross"), type: "num" },
                                { key: "avg_ticket", label: t("pi_avg_ticket_short"), type: "num" },
                                { key: "in_store_total", label: t("pi_kpi_in_store"), type: "num" },
                                { key: "apps_total", label: t("pi_kpi_apps"), type: "num" },
                                { key: "waste_total", label: t("pi_kpi_waste"), type: "num" },
                                { key: "discount_total", label: t("pi_kpi_discounts"), type: "num" },
                            ]}
                            shareKey="gross"
                            rowTestId={(r) => PI.branchRow(r.pos_branch_id)}
                        />
                    </PosCard>

                    <Tabs defaultValue="hourly" className="space-y-4">
                        <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto">
                            {[
                                ["hourly", t("pi_tab_hourly")],
                                ["items", t("pi_tab_items")],
                                ["categories", t("pi_tab_categories")],
                                ["channels", t("pi_tab_channels")],
                                ["staff", t("pi_tab_staff")],
                                ["cashier_check", t("pi_tab_cashier_check")],
                                ["data", t("pi_tab_data")],
                            ].map(([k, label]) => (
                                <TabsTrigger key={k} data-testid={PI.tab(k)} value={k} className={triggerClass}>{label}</TabsTrigger>
                            ))}
                        </TabsList>

                        <TabsContent value="hourly" className="mt-0 focus-visible:outline-none">
                            <PosHourlyPanel data={data} />
                        </TabsContent>
                        <TabsContent value="items" className="mt-0 focus-visible:outline-none">
                            <PosItemsPanel range={range} branchId={branchId} />
                        </TabsContent>
                        <TabsContent value="categories" className="mt-0 focus-visible:outline-none">
                            <PosCategoriesPanel data={data} />
                        </TabsContent>
                        <TabsContent value="channels" className="mt-0 focus-visible:outline-none">
                            <PosChannelsPanel data={data} />
                        </TabsContent>
                        <TabsContent value="staff" className="mt-0 focus-visible:outline-none">
                            <PosStaffPanel data={data} />
                        </TabsContent>
                        <TabsContent value="cashier_check" className="mt-0 focus-visible:outline-none">
                            <PosCashierCheckPanel branchId={branchId} />
                        </TabsContent>
                        <TabsContent value="data" className="mt-0 focus-visible:outline-none">
                            <PosDataPanel data={data} />
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
        </DataScope>
    );
}
